import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleBySlug } from '@/mocks/articles';

function formatViews(views: number): string {
  if (views >= 10000) {
    return (views / 10000).toFixed(1) + '万';
  }
  return views.toString();
}

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-950">文章不存在</h1>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = [article].filter(Boolean);

  return (
    <>
      <Navbar />
      <main>
        <article className="w-full bg-background-50">
          <div className="w-full px-4 md:px-8 max-w-[1000px] mx-auto pt-24 md:pt-28 pb-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.category, href: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />
          </div>

          <div className="w-full px-4 md:px-8 max-w-[800px] mx-auto pb-8">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-accent-100 text-secondary-950 text-xs font-semibold rounded-full whitespace-nowrap">
                    #{tag}
                  </span>
                ))}
              </div>

              <p className="text-sm font-heading italic text-foreground-500 mb-3">
                {article.category} · 深度报道
              </p>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-black font-sans text-foreground-950 leading-tight">
                {article.title.split('，')[0]}
                <span className="font-heading italic text-primary-500"> {article.title.includes('，') ? article.title.split('，')[1]?.substring(0, 15) : ''}</span>
              </h1>
              <div className="flex items-center gap-6 mt-5 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-calendar-line text-sm"></i>
                  </span>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-eye-line text-sm"></i>
                  </span>
                  {formatViews(article.views)} 阅读
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-chat-3-line text-sm"></i>
                  </span>
                  {formatViews(article.comments)} 评论
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-time-line text-sm"></i>
                  </span>
                  {article.readTime}
                </span>
              </div>
            </div>
          </div>

          <div className="w-full px-4 md:px-8 max-w-[800px] mx-auto">
            <div className="w-full rounded-2xl overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-auto object-cover"
              />
            </div>
          </div>

          <div className="w-full px-4 md:px-8 max-w-[800px] mx-auto pb-12">
            <div className="prose prose-lg max-w-none text-foreground-800 leading-relaxed">
              <p className="text-lg leading-loose mb-6">
                {article.excerpt}
              </p>
              <p className="text-base leading-loose mb-6">
                据知情人士透露，此事在业内已经传了一段时间，但一直未得到官方证实。直到近日，相关方终于打破沉默，对外界猜测做出了正式回应。这一消息的确认，立即在社交媒体上引发轩然大波，相关话题迅速登上各大平台热搜榜首位。
              </p>
              <p className="text-base leading-loose mb-6">
                多位圈内人士在接受采访时表示，这一事件的影响远超预期，可能会对整个行业格局产生深远影响。有分析人士指出，在当前的大环境下，此类事件的发生并非偶然，而是多种因素长期积累的结果。
              </p>
              <blockquote className="border-l-4 border-primary-500 pl-6 py-2 my-8 text-foreground-600 italic font-heading text-lg">
                "这件事的发酵速度之快，确实超出了所有人的预料。但仔细想想，一切都有迹可循。"
                <footer className="mt-2 text-sm font-sans not-italic text-foreground-400">— 一位不愿透露姓名的业内人士</footer>
              </blockquote>
              <p className="text-base leading-loose mb-6">
                从目前掌握的信息来看，事件的核心在于各方利益的博弈。早在几个月前，就有零星的传闻开始在网络上流传，但当时并未引起太多关注。随着时间的推移，越来越多的细节被曝光，公众的关注度也随之水涨船高。
              </p>
              <p className="text-base leading-loose mb-6">
                值得一提的是，粉丝群体在这次事件中扮演了重要角色。他们通过社交媒体平台自发组织，形成了强大的舆论力量。不少网友表示，这次事件的走向将直接影响他们对相关方的信任度。
              </p>
            </div>
          </div>

          <div className="w-full bg-background-50 py-12">
            <div className="w-full px-4 md:px-8 max-w-[800px] mx-auto">
              <div className="w-full rounded-2xl overflow-hidden mb-10">
                <img
                  src="https://readdy.ai/api/search-image?query=Artistic%20film%20grain%20texture%20on%20soft%20warm%20abstract%20composition%2C%20cinematic%20mood%2C%20golden%20hour%20light%2C%20dreamy%20atmosphere%2C%20professional%20photography%2C%20neutral%20cream%20and%20beige%20tones&width=1200&height=600&seq=article-media-002&orientation=landscape"
                  alt="相关内容配图"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>
              <div className="text-center mb-10">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 leading-relaxed">
                  <span className="font-light text-foreground-400">更多精彩</span> 持续追踪
                  <br />
                  <span className="font-heading italic font-bold text-primary-500">关注我们</span> 不错过每个热点
                </h2>
              </div>
            </div>
          </div>

          <div className="w-full bg-background-100 py-12">
            <div className="w-full px-4 md:px-8 max-w-[1000px] mx-auto">
              <h2 className="text-xl font-bold text-foreground-950 mb-6">相关标签</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/category/${article.categorySlug}`}
                    className="block bg-background-50 rounded-xl p-5 transition-all duration-200 cursor-pointer"
                  >
                    <span className="w-10 h-10 flex items-center justify-center">
                      <i className="ri-hashtag text-xl text-foreground-950"></i>
                    </span>
                    <h3 className="mt-3 text-sm font-bold text-foreground-950">#{tag}</h3>
                    <p className="mt-1 text-xs text-foreground-500">查看相关内容</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </article>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: article.title,
              description: article.excerpt,
              datePublished: article.date,
              author: {
                '@type': 'Organization',
                name: '91免费',
              },
              publisher: {
                '@type': 'Organization',
                name: '91免费',
              },
              mainEntityOfPage: {
                '@type': 'WebPage',
                '@id': `https://www.91free.com/article/${article.slug}`,
              },
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://www.91free.com',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: article.category,
                  item: `https://www.91free.com/category/${article.categorySlug}`,
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name: article.title,
                },
              ],
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}